import React, { useMemo, useState, useEffect } from "react";
import { useGlobalContext } from "../Config";

import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { formatDateCL } from "../utilities/DateUtil";

const EstadoMedicion = () => {
  const { baseUrl } = useGlobalContext();

  const [isLoading, setIsLoading] = useState(false);

  const [empresaId, setEmpresaId] = useState(null);

  // Mes/Año (como ConsumoMedidor)
  const [mes, setMes] = useState(null); // 1..12
  const [anio, setAnio] = useState(null); // 2024..2026

  // tipo opcional
  const [tipoId, setTipoId] = useState(null);
  const [tipos, setTipos] = useState([]);

  const [resp, setResp] = useState(null); // { total, leidos, pendientes, itemsLeidos, itemsPendientes, ... }
  const [activeIndex, setActiveIndex] = useState(0);

  const empresasOptions = [
    { label: "ALTAMIRA", value: 1 },
    { label: "LA SIEMBRA", value: 2 },
  ];

  const mesesOptions = [
    { label: "Enero", value: 1 },
    { label: "Febrero", value: 2 },
    { label: "Marzo", value: 3 },
    { label: "Abril", value: 4 },
    { label: "Mayo", value: 5 },
    { label: "Junio", value: 6 },
    { label: "Julio", value: 7 },
    { label: "Agosto", value: 8 },
    { label: "Septiembre", value: 9 },
    { label: "Octubre", value: 10 },
    { label: "Noviembre", value: 11 },
    { label: "Diciembre", value: 12 },
  ];

  const aniosOptions = [
    { label: "2024", value: 2024 },
    { label: "2025", value: 2025 },
    { label: "2026", value: 2026 },
  ];

  const fetchJson = async (url, options = {}) => {
    const res = await fetch(url, {
      credentials: "include",
      redirect: "follow",
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.respuesta === false) {
      const msg = data?.mensaje || `Error HTTP ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  // (Opcional) cargar tipos desde tu backend si ya existe tipo/buscar
  useEffect(() => {
    (async () => {
      try {
        // si no tienes este endpoint, comenta este bloque y usa opciones fijas
        const data = await fetchJson(`${baseUrl}tipo/buscar`, {
          method: "GET",
        });
        setTipos(Array.isArray(data?.tipos) ? data.tipos : []);
      } catch (e) {
        // silencioso: dejamos tipos vacío y el filtro igual funciona como "Todos"
        setTipos([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tiposOptions = useMemo(() => {
    const base = [{ label: "Todos", value: null }];
    const dyn = (tipos || []).map((t) => ({
      label: t?.descripcion ?? `Tipo ${t?.id}`,
      value: t?.id ?? null,
    }));
    return base.concat(dyn);
  }, [tipos]);

  const validar = () => {
    if (!empresaId) {
      alert("Debe seleccionar empresa.");
      return false;
    }
    if (!anio || !mes) {
      alert("Debe seleccionar mes y año.");
      return false;
    }
    return true;
  };

  const buscar = async () => {
    if (!validar()) return;

    setIsLoading(true);
    try {
      // endpoint sugerido: estadoMediciones?empresaId=...&anio=...&mes=...&tipoId=...
      let url =
        `${baseUrl}estadoMediciones` +
        `?empresaId=${encodeURIComponent(empresaId)}` +
        `&anio=${encodeURIComponent(anio)}` +
        `&mes=${encodeURIComponent(mes)}`;

      if (tipoId != null) url += `&tipoId=${encodeURIComponent(tipoId)}`;

      const data = await fetchJson(url, { method: "GET" });

      // tu controller debería devolver: total, leidos, pendientes, itemsLeidos, itemsPendientes, periodo, etc.
      setResp(data);
      setActiveIndex(0);
    } catch (e) {
      console.error(e);
      alert("Error consultando estado mediciones: " + (e?.message || e));
      setResp(null);
    } finally {
      setIsLoading(false);
    }
  };

  const limpiar = () => {
    setEmpresaId(null);
    setMes(null);
    setAnio(null);
    setTipoId(null);
    setResp(null);
    setActiveIndex(0);
  };

  const tablaProps = {
    responsiveLayout: "scroll",
    className: "p-datatable-sm",
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 20],
    stripedRows: true,
    showGridlines: true,
  };

  const periodoLabel = useMemo(() => {
    if (!anio || !mes) return "-";
    return `${anio}-${String(mes).padStart(2, "0")}`;
  }, [anio, mes]);

  const itemsLeidos = Array.isArray(resp?.itemsLeidos) ? resp.itemsLeidos : [];
  const itemsPend = Array.isArray(resp?.itemsPendientes)
    ? resp.itemsPendientes
    : [];

  const cardStyle = {
    padding: "14px 16px",
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
  };

  const big = { fontSize: 22, fontWeight: 800, marginTop: 6 };

  const lecturaFechaBody = (row) => {
    const v = row?.lecturaFecha;
    return v ? formatDateCL(v) : "-";
  };

  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1; // 1..12

    if (y >= 2024 && y <= 2026) {
      setAnio(y);
      setMes(m);
    }
  }, []);

  const TIPO_LABEL = {
    1: "AGUA",
    2: "ELECTRICIDAD",
  };

  const total = Number(resp?.total ?? itemsLeidos.length + itemsPend.length);
  const leidosCount = Number(resp?.leidos ?? itemsLeidos.length);
  const pendientesCount = Number(resp?.pendientes ?? itemsPend.length);

  const pctLeidos = total > 0 ? Math.round((leidosCount * 100) / total) : 0;

  return (
    <div className="col-12 md:col-12">
      <div
        style={{
          padding: "20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
          marginBottom: "20px",
        }}
      >
        <div>
          <h4 style={{ margin: 0, color: "#495057" }}>Estado Mediciones</h4>
          <small style={{ color: "#6c757d" }}>
            Revisa qué remarcadores fueron leídos y cuáles están pendientes por
            periodo
          </small>
        </div>
      </div>

      {/* filtros */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-3">
            <label style={{ fontWeight: "bold" }}>Empresa</label>
            <Dropdown
              value={empresaId}
              options={empresasOptions}
              onChange={(e) => setEmpresaId(e.value)}
              placeholder="Seleccione empresa"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-3">
            <label style={{ fontWeight: "bold" }}>Mes</label>
            <Dropdown
              value={mes}
              options={mesesOptions}
              onChange={(e) => setMes(e.value)}
              placeholder="Seleccione mes"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-2">
            <label style={{ fontWeight: "bold" }}>Año</label>
            <Dropdown
              value={anio}
              options={aniosOptions}
              onChange={(e) => setAnio(e.value)}
              placeholder="Año"
              style={{ width: "100%" }}
              showClear
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-2">
            <label style={{ fontWeight: "bold" }}>Tipo (opcional)</label>
            <Dropdown
              value={tipoId}
              options={tiposOptions}
              onChange={(e) => setTipoId(e.value)}
              placeholder="Todos"
              style={{ width: "100%" }}
              showClear={false}
              disabled={isLoading}
            />
          </div>

          <div className="col-12 md:col-2">
            <label style={{ fontWeight: "bold", opacity: 0 }}>Acciones</label>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                height: "42px",
                marginTop: 4,
              }}
            >
              <Button
                label="Buscar"
                icon="pi pi-search"
                onClick={buscar}
                disabled={isLoading}
              />
              <Button
                label="Limpiar"
                icon="pi pi-times"
                className="p-button-text"
                onClick={limpiar}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <ProgressSpinner />
        </div>
      )}

      {/* cards */}
      <div className="grid" style={{ marginBottom: 20 }}>
        <div className="col-12 md:col-3">
          <div style={cardStyle}>
            <div style={{ color: "#6c757d", fontWeight: 700 }}>Total</div>
            <div style={big}>{resp?.total ?? 0}</div>
            <div style={{ color: "#6c757d", fontSize: 12 }}>
              Periodo: {resp?.periodo || periodoLabel}
            </div>
          </div>
        </div>

        <div className="col-12 md:col-3">
          <div style={cardStyle}>
            <div style={{ color: "#6c757d", fontWeight: 700 }}>Leídos</div>
            <div style={big}>{resp?.leidos ?? 0}</div>
            <div style={{ color: "#6c757d", fontSize: 12 }}>
              Con lectura registrada en el periodo
            </div>
          </div>
        </div>

        <div className="col-12 md:col-3">
          <div style={cardStyle}>
            <div style={{ color: "#6c757d", fontWeight: 700 }}>Pendientes</div>
            <div style={big}>{resp?.pendientes ?? 0}</div>
            <div style={{ color: "#6c757d", fontSize: 12 }}>
              Sin lectura registrada en el periodo
            </div>
          </div>
        </div>
        <div className="col-12 md:col-3">
          <div style={cardStyle}>
            <div style={{ color: "#6c757d", fontWeight: 700 }}>% Leídos</div>
            <div style={big}>{pctLeidos}%</div>
            <div style={{ color: "#6c757d", fontSize: 12 }}>
              {leidosCount} de {total}
            </div>
          </div>
        </div>
      </div>

      {/* tablas */}
      <div
        style={{
          padding: "20px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.05)",
          marginBottom: "20px",
        }}
      >
        <TabView
          activeIndex={activeIndex}
          onTabChange={(e) => setActiveIndex(e.index)}
        >
          <TabPanel header={`Leídos (${itemsLeidos.length})`}>
            <DataTable
              value={itemsLeidos}
              dataKey={(row) => String(row?.remarcadorId ?? "")}
              {...tablaProps}
            >
              <Column field="remarcadorId" header="Remarcador ID" sortable />
              <Column field="qrCode" header="QR" sortable />
              <Column field="medidorPadreId" header="Medidor padre" sortable />
              <Column
                header="Tipo"
                body={(row) =>
                  TIPO_LABEL[row?.tipoId] ?? String(row?.tipoId ?? "-")
                }
                sortable
                sortField="tipoId"
              />
              <Column
                header="Fecha lectura"
                body={(row) => row?.lecturaFecha ?? "-"}
                sortable
                sortField="lecturaFecha"
              />
              {/*<Column field="lecturaUsuarioId" header="Usuario" sortable />*/}
            </DataTable>
          </TabPanel>

          <TabPanel header={`Pendientes (${itemsPend.length})`}>
            <DataTable
              value={itemsPend}
              dataKey={(row) => String(row?.remarcadorId ?? "")}
              {...tablaProps}
            >
              <Column field="remarcadorId" header="Remarcador ID" sortable />
              <Column field="qrCode" header="QR" sortable />
              <Column field="medidorPadreId" header="Medidor padre" sortable />
              <Column field="tipoId" header="Tipo" sortable />
            </DataTable>
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
};

export default EstadoMedicion;
